import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import MovieCard from '../components/MovieCard';
import Filters from '../components/Filters';
import Pagination from '../components/Pagination';
import MediaModal from '../components/MediaModal';
import { discoverMovies, fetchMovieDetails } from '../services/tmdb';

// ========== TRANSLATIONS ==========
const translations = {
  en: {
    filterLabel: 'FILTERS',
    noFiltersApplied: 'No filters applied',
    loadingMovies: 'Loading movies...',
    noMoviesFound: 'No movies found with the selected filters',
    searchMovies: 'Search Movies',
    genre: 'Genre',
    language: 'Language',
    releaseYear: 'Release Year',
    sortBy: 'Sort By',
    provider: 'Provider',
    allProviders: 'All Providers',
    allGenres: 'All Genres',
    allLanguages: 'All Languages',
    allYears: 'All Years',
    movieGenres: {
      '': 'All Genres',
      '28': 'Action',
      '12': 'Adventure',
      '16': 'Animation',
      '35': 'Comedy',
      '80': 'Crime',
      '99': 'Documentary',
      '18': 'Drama',
      '10751': 'Family',
      '14': 'Fantasy',
      '36': 'History',
      '27': 'Horror',
      '10402': 'Music',
      '9648': 'Mystery',
      '10749': 'Romance',
      '878': 'Science Fiction',
      '10770': 'TV Movie',
      '53': 'Thriller',
      '10752': 'War',
      '37': 'Western',
    },
    languages: {
      '': 'All Languages',
      en: 'English',
      es: 'Spanish',
      fr: 'French',
      de: 'German',
      it: 'Italian',
      ja: 'Japanese',
      ko: 'Korean',
      zh: 'Chinese',
      ru: 'Russian',
    },
    years: {
      '': 'All Years',
      '<1989': '<1990',
      '1990-1999': '1990-2000',
      '2000-2009': '2000-2010',
      '2010-2019': '2010-2020',
      '2020-present': '2020-Present',
    },
    movieSortOptions: {
      'popularity.desc': 'Popularity',
      'revenue.desc': 'Box Office (Highest Grossing)',
      'vote_average.desc': 'Highest Rated (70%+)',
      everything: 'Indie & Lesser Known',
    },
    providers: {
      '': 'All Providers (Recommended)',
      '8': 'Netflix',
      '337': 'Disney+',
      '350': 'Apple TV+',
      '43': 'Starz',
      '15': 'Hulu',
      '1899': 'HBO Max',
      '528': 'AMC+',
      '9': 'Amazon Prime Video',
      '386': 'Peacock',
      '283': 'Crunchyroll',
      '99': 'Shudder',
    },
  },
  fr: {
    filterLabel: 'FILTRES',
    noFiltersApplied: 'Aucun filtre appliqué',
    loadingMovies: 'Chargement des films...',
    noMoviesFound: 'Aucun film trouvé avec les filtres sélectionnés',
    searchMovies: 'Rechercher des films',
    genre: 'Genre',
    language: 'Langue',
    releaseYear: 'Année de sortie',
    sortBy: 'Trier par',
    provider: 'Fournisseur',
    allProviders: 'Tous les fournisseurs',
    allGenres: 'Tous les genres',
    allLanguages: 'Toutes les langues',
    allYears: 'Toutes les années',
    movieGenres: {
      '': 'Tous les genres',
      '28': 'Action',
      '12': 'Aventure',
      '16': 'Animation',
      '35': 'Comédie',
      '80': 'Crime',
      '99': 'Documentaire',
      '18': 'Drame',
      '10751': 'Famille',
      '14': 'Fantaisie',
      '36': 'Histoire',
      '27': 'Horreur',
      '10402': 'Musique',
      '9648': 'Mystère',
      '10749': 'Romance',
      '878': 'Science-Fiction',
      '10770': 'Téléfilm',
      '53': 'Thriller',
      '10752': 'Guerre',
      '37': 'Western',
    },
    languages: {
      '': 'Toutes les langues',
      en: 'Anglais',
      es: 'Espagnol',
      fr: 'Français',
      de: 'Allemand',
      it: 'Italien',
      ja: 'Japonais',
      ko: 'Coréen',
      zh: 'Chinois',
      ru: 'Russe',
    },
    years: {
      '': 'Toutes les années',
      '<1989': '<1990',
      '1990-1999': '1990-2000',
      '2000-2009': '2000-2010',
      '2010-2019': '2010-2020',
      '2020-present': '2020-Présent',
    },
    movieSortOptions: {
      'popularity.desc': 'Populaire',
      'revenue.desc': 'Box-office (plus gros revenus)',
      'vote_average.desc': 'Mieux notés (70%+)',
      everything: 'Indie & Moins Connus',
    },
    providers: {
      '': 'Tous les fournisseurs',
      '8': 'Netflix',
      '337': 'Disney+',
      '350': 'Apple TV+',
      '43': 'Starz',
      '15': 'Hulu',
      '1899': 'HBO Max',
      '528': 'AMC+',
      '9': 'Amazon Prime Video',
      '386': 'Peacock',
      '283': 'Crunchyroll',
      '500': 'Shudder',
      '531': 'Paramount+',
    },
  },
  es: {
    filterLabel: 'FILTROS',
    noFiltersApplied: 'Sin filtros aplicados',
    loadingMovies: 'Cargando películas...',
    noMoviesFound: 'No se encontraron películas con los filtros seleccionados',
    searchMovies: 'Buscar películas',
    genre: 'Género',
    language: 'Idioma',
    releaseYear: 'Año de estreno',
    sortBy: 'Ordenar por',
    provider: 'Proveedor',
    allProviders: 'Todos los proveedores',
    allGenres: 'Todos los géneros',
    allLanguages: 'Todos los idiomas',
    allYears: 'Todos los años',
    movieGenres: {
      '': 'Todos los géneros',
      '28': 'Acción',
      '12': 'Aventura',
      '16': 'Animación',
      '35': 'Comedia',
      '80': 'Crimen',
      '99': 'Documental',
      '18': 'Drama',
      '10751': 'Familia',
      '14': 'Fantasía',
      '36': 'Historia',
      '27': 'Terror',
      '10402': 'Música',
      '9648': 'Misterio',
      '10749': 'Romance',
      '878': 'Ciencia Ficción',
      '10770': 'Película de TV',
      '53': 'Suspenso',
      '10752': 'Guerra',
      '37': 'Western',
    },
    languages: {
      '': 'Todos los idiomas',
      en: 'Inglés',
      es: 'Español',
      fr: 'Francés',
      de: 'Alemán',
      it: 'Italiano',
      ja: 'Japonés',
      ko: 'Coreano',
      zh: 'Chino',
      ru: 'Ruso',
    },
    years: {
      '': 'Todos los años',
      '<1989': '<1990',
      '1990-1999': '1990-2000',
      '2000-2009': '2000-2010',
      '2010-2019': '2010-2020',
      '2020-present': '2020-Presente',
    },
    movieSortOptions: {
      'popularity.desc': 'Popular',
      'revenue.desc': 'Taquilla (mayor recaudación)',
      'vote_average.desc': 'Mejor valoradas (70%+)',
      everything: 'Indie & Menos Conocidos',
    },
    providers: {
      '': 'Todos los proveedores',
      '8': 'Netflix',
      '337': 'Disney+',
      '350': 'Apple TV+',
      '43': 'Starz',
      '15': 'Hulu',
      '1899': 'HBO Max',
      '528': 'AMC+',
      '9': 'Amazon Prime Video',
      '386': 'Peacock',
      '283': 'Crunchyroll',
      '500': 'Shudder',
      '531': 'Paramount+',
    },
  },
  it: {
    filterLabel: 'FILTRI',
    noFiltersApplied: 'Nessun filtro applicato',
    loadingMovies: 'Caricamento film...',
    noMoviesFound: 'Nessun film trovato con i filtri selezionati',
    searchMovies: 'Cerca film',
    genre: 'Genere',
    language: 'Lingua',
    releaseYear: 'Anno di uscita',
    sortBy: 'Ordina per',
    provider: 'Fornitore',
    allProviders: 'Tutti i fornitori',
    allGenres: 'Tutti i generi',
    allLanguages: 'Tutte le lingue',
    allYears: 'Tutti gli anni',
    movieGenres: {
      '': 'Tutti i generi',
      '28': 'Azione',
      '12': 'Avventura',
      '16': 'Animazione',
      '35': 'Commedia',
      '80': 'Crimine',
      '99': 'Documentario',
      '18': 'Dramma',
      '10751': 'Famiglia',
      '14': 'Fantasy',
      '36': 'Storia',
      '27': 'Horror',
      '10402': 'Musica',
      '9648': 'Mistero',
      '10749': 'Romance',
      '878': 'Fantascienza',
      '10770': 'Film TV',
      '53': 'Thriller',
      '10752': 'Guerra',
      '37': 'Western',
    },
    languages: {
      '': 'Tutte le lingue',
      en: 'Inglese',
      es: 'Spagnolo',
      fr: 'Francese',
      de: 'Tedesco',
      it: 'Italiano',
      ja: 'Giapponese',
      ko: 'Coreano',
      zh: 'Cinese',
      ru: 'Russo',
    },
    years: {
      '': 'Tutti gli anni',
      '<1989': '<1990',
      '1990-1999': '1990-2000',
      '2000-2009': '2000-2010',
      '2010-2019': '2010-2020',
      '2020-present': '2020-Presente',
    },
    movieSortOptions: {
      'popularity.desc': 'Popolare',
      'revenue.desc': 'Incasso (più alto)',
      'vote_average.desc': 'Più votati (70%+)',
      everything: 'Indie & Meno Conosciuti',
    },
    providers: {
      '': 'Tutti i fornitori',
      '8': 'Netflix',
      '337': 'Disney+',
      '350': 'Apple TV+',
      '43': 'Starz',
      '15': 'Hulu',
      '1899': 'HBO Max',
      '528': 'AMC+',
      '9': 'Amazon Prime Video',
      '386': 'Peacock',
      '283': 'Crunchyroll',
      '500': 'Shudder',
      '531': 'Paramount+',
    },
  },
  nl: {
    filterLabel: 'FILTERS',
    noFiltersApplied: 'Geen filters toegepast',
    loadingMovies: 'Films laden...',
    noMoviesFound: 'Geen films gevonden met de geselecteerde filters',
    searchMovies: 'Zoek films',
    genre: 'Genre',
    language: 'Taal',
    releaseYear: 'Releasejaar',
    sortBy: 'Sorteer op',
    provider: 'Provider',
    allProviders: 'Alle providers',
    allGenres: 'Alle genres',
    allLanguages: 'Alle talen',
    allYears: 'Alle jaren',
    movieGenres: {
      '': 'Alle genres',
      '28': 'Actie',
      '12': 'Avontuur',
      '16': 'Animatie',
      '35': 'Komedie',
      '80': 'Misdaad',
      '99': 'Documentaire',
      '18': 'Drama',
      '10751': 'Familie',
      '14': 'Fantasy',
      '36': 'Geschiedenis',
      '27': 'Horror',
      '10402': 'Muziek',
      '9648': 'Mysterie',
      '10749': 'Romantiek',
      '878': 'Sciencefiction',
      '10770': 'TV-film',
      '53': 'Thriller',
      '10752': 'Oorlog',
      '37': 'Western',
    },
    languages: {
      '': 'Alle talen',
      en: 'Engels',
      es: 'Spaans',
      fr: 'Frans',
      de: 'Duits',
      it: 'Italiaans',
      ja: 'Japans',
      ko: 'Koreaans',
      zh: 'Chinees',
      ru: 'Russisch',
    },
    years: {
      '': 'Alle jaren',
      '<1989': '<1990',
      '1990-1999': '1990-2000',
      '2000-2009': '2000-2010',
      '2010-2019': '2010-2020',
      '2020-present': '2020-Heden',
    },
    movieSortOptions: {
      'popularity.desc': 'Populair',
      'revenue.desc': 'Kassa (hoogste opbrengst)',
      'vote_average.desc': 'Hoogst gewaardeerd (70%+)',
      everything: 'Indie & Minder Bekend',
    },
    providers: {
      '': 'Alle providers',
      '8': 'Netflix',
      '337': 'Disney+',
      '350': 'Apple TV+',
      '43': 'Starz',
      '15': 'Hulu',
      '1899': 'HBO Max',
      '528': 'AMC+',
      '9': 'Amazon Prime Video',
      '386': 'Peacock',
      '283': 'Crunchyroll',
      '500': 'Shudder',
      '531': 'Paramount+',
    },
  },
};

// ========== SESSION STORAGE HELPERS ==========
const getSessionValue = (key, defaultValue) => {
  try {
    const stored = sessionStorage.getItem(key);
    return stored !== null ? JSON.parse(stored) : defaultValue;
  } catch {
    return defaultValue;
  }
};

const setSessionValue = (key, value) => {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    // ignore
  }
};

// ========== CACHE HELPERS (localStorage, 12h TTL) ==========
const CACHE_KEY = 'movies_default_cache';
const CACHE_TTL = 12 * 60 * 60 * 1000; // 12 hours

const getCachedMovies = () => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (Date.now() - data.timestamp > CACHE_TTL) {
      localStorage.removeItem(CACHE_KEY);
      return null;
    }
    return data.movies;
  } catch {
    return null;
  }
};

const setCachedMovies = (movies) => {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({
      timestamp: Date.now(),
      movies,
    }));
  } catch {
    // ignore
  }
};

const BLOCKED_MOVIE_IDS = [];

const MoviesPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize state from URL params
  const [movies, setMovies] = useState([]);
  const [currentPage, setCurrentPage] = useState(() => {
    const page = parseInt(searchParams.get('page'), 10);
    return isNaN(page) || page < 1 ? 1 : page;
  });
  const [totalPages, setTotalPages] = useState(1);

  const [genre, setGenre] = useState(() => searchParams.get('genre') || '');
  const [language, setLanguage] = useState(() => searchParams.get('language') || '');
  const [year, setYear] = useState(() => searchParams.get('year') || '');
  const [sort, setSort] = useState(() => searchParams.get('sort') || 'popularity.desc');
  const [provider, setProvider] = useState(() => searchParams.get('provider') || '');

  // ----- Modal state (unified) -----
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [selectedMediaType, setSelectedMediaType] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // ----- Ref to prevent duplicate modal openings -----
  const isOpeningRef = useRef(false);

  const [filtersMinimized, setFiltersMinimized] = useState(true);
  const [loading, setLoading] = useState(true);

  const filtersRef = useRef(null);
  const collapsedFiltersBtnRef = useRef(null);

  // Language
  const [lang, setLang] = useState(() => {
    return localStorage.getItem('preferredLanguage') || 'en';
  });

  // Rating format (stars or number)
  const [ratingFormat, setRatingFormat] = useState(() => {
    return localStorage.getItem('ratingFormat') || 'stars';
  });

  // ---------- PAGINATION COOLDOWN ----------
  const [paginationCooldown, setPaginationCooldown] = useState(false);
  const cooldownTimeoutRef = useRef(null);

  const handlePaginationAction = useCallback((action) => {
    if (paginationCooldown) return;
    setPaginationCooldown(true);
    if (cooldownTimeoutRef.current) {
      clearTimeout(cooldownTimeoutRef.current);
    }
    cooldownTimeoutRef.current = setTimeout(() => {
      setPaginationCooldown(false);
      cooldownTimeoutRef.current = null;
    }, 2000);
    action();
  }, [paginationCooldown]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (cooldownTimeoutRef.current) {
        clearTimeout(cooldownTimeoutRef.current);
      }
    };
  }, []);

  const t = (key) => {
    const langData = translations[lang] || translations.en;
    return langData[key] || key;
  };

  const tNested = (objKey, key) => {
    const langData = translations[lang] || translations.en;
    const obj = langData[objKey];
    return obj ? obj[key] : key;
  };

  // Update URL params whenever state changes
  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    if (currentPage > 1) params.set('page', currentPage.toString());
    else params.delete('page');

    if (genre) params.set('genre', genre);
    else params.delete('genre');
    if (language) params.set('language', language);
    else params.delete('language');
    if (year) params.set('year', year);
    else params.delete('year');
    if (sort !== 'popularity.desc') params.set('sort', sort);
    else params.delete('sort');
    if (provider) params.set('provider', provider);
    else params.delete('provider');

    setSearchParams(params, { replace: true });
  }, [currentPage, genre, language, year, sort, provider, searchParams, setSearchParams]);

  // Capture scroll position on unmount / before unload
  useEffect(() => {
    const saveScroll = () => {
      setSessionValue('moviesPage_scrollPos', window.scrollY);
    };
    window.addEventListener('beforeunload', saveScroll);
    return () => {
      saveScroll();
      window.removeEventListener('beforeunload', saveScroll);
    };
  }, []);

  // Restore scroll after initial data load
  const initialScrollRef = useRef(true);
  useEffect(() => {
    if (!loading && initialScrollRef.current) {
      initialScrollRef.current = false;
      const savedScroll = getSessionValue('moviesPage_scrollPos', 0);
      if (savedScroll > 0) {
        window.scrollTo(0, savedScroll);
      }
    }
  }, [loading]);

  // Reset page to 1 only when filters actually change (including provider)
  const prevFilters = useRef({ genre, language, year, sort, provider });

  useEffect(() => {
    const prev = prevFilters.current;
    const changed =
      genre !== prev.genre ||
      language !== prev.language ||
      year !== prev.year ||
      sort !== prev.sort ||
      provider !== prev.provider;

    if (changed) {
      setCurrentPage(1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    prevFilters.current = { genre, language, year, sort, provider };
  }, [genre, language, year, sort, provider]);

  // Listen to language & ratingFormat changes from localStorage
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'preferredLanguage') {
        setLang(e.newValue || 'en');
      }
      if (e.key === 'ratingFormat') {
        setRatingFormat(e.newValue || 'stars');
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // ----- Watch URL params for modal opening (both movie and series) -----
  useEffect(() => {
    const movieId = searchParams.get('movie');
    const seriesId = searchParams.get('series');
    if (movieId && !isNaN(parseInt(movieId))) {
      openContentModal(parseInt(movieId), 'movie');
    } else if (seriesId && !isNaN(parseInt(seriesId))) {
      openContentModal(parseInt(seriesId), 'tv');
    } else {
      // If neither param is present, we might want to close the modal if it's open,
      // but we only close when we explicitly call closeModal.
    }
  }, [searchParams]); // run whenever URL params change

  const getActiveFiltersText = () => {
    const activeFilters = [];
    if (genre) {
      const genreName = tNested('movieGenres', genre);
      if (genreName) activeFilters.push(genreName);
    }
    if (language) {
      const langName = tNested('languages', language);
      if (langName) activeFilters.push(langName);
    }
    if (year) {
      const yearName = tNested('years', year);
      if (yearName) activeFilters.push(yearName);
    }
    if (sort) {
      const sortName = tNested('movieSortOptions', sort);
      if (sortName) activeFilters.push(sortName);
    }
    if (provider) {
      const providerName = tNested('providers', provider);
      if (providerName) activeFilters.push(providerName);
    }
    return activeFilters.length > 0 ? activeFilters.join(' • ') : t('noFiltersApplied');
  };

  // ===== FETCH MOVIES with provider and caching for default page =====
  const fetchMovies = useCallback(async () => {
    const isDefaultPage = currentPage === 1 && !genre && !language && !year && sort === 'popularity.desc' && !provider;

    // Try cache only for default page
    if (isDefaultPage) {
      const cached = getCachedMovies();
      if (cached) {
        setMovies(cached);
        setTotalPages(Math.min(500, 500)); // approx
        setLoading(false);
        return;
      }
    }

    setLoading(true);
    const filters = { genre, language, year, sort, provider };
    try {
      const data = await discoverMovies(currentPage, filters);
      let results = data.results || [];
      // Exclude Hindi movies when no language filter is selected
      if (!language) {
        results = results.filter(movie => movie.original_language !== 'hi');
      }

      // For all pages, fetch details to get runtime
      const detailedResults = await Promise.all(
        results.map(async (movie) => {
          try {
            const details = await fetchMovieDetails(movie.id);
            return {
              ...movie,
              runtime: details.runtime || 0,
              release_year: details.release_date ? new Date(details.release_date).getFullYear() : null,
            };
          } catch {
            return {
              ...movie,
              runtime: 0,
              release_year: movie.release_date ? new Date(movie.release_date).getFullYear() : null,
            };
          }
        })
      );

      if (isDefaultPage) {
        setCachedMovies(detailedResults);
      }
      setMovies(detailedResults);
      setTotalPages(Math.min(data.total_pages, 800));
    } catch (error) {
      console.error('Error fetching movies:', error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, genre, language, year, sort, provider]);

  useEffect(() => {
    fetchMovies();
  }, [fetchMovies]);

  // ===== Modal functions =====
  const openContentModal = async (contentId, contentType) => {
    if (isOpeningRef.current) return; // prevent duplicate calls
    isOpeningRef.current = true;
    try {
      let details;
      if (contentType === 'movie') {
        details = await fetchMovieDetails(contentId);
      } else if (contentType === 'tv') {
        details = await fetch(`https://api.themoviedb.org/3/tv/${contentId}?api_key=57240db50c2008e78c261e1a934627e4&language=en-US`).then(res => res.json());
      }
      if (details) {
        setSelectedMedia({ ...details, media_type: contentType });
        setSelectedMediaType(contentType);
        setIsModalOpen(true);
        // Update URL params (the effect above will re-run, but we already set them via searchParams)
        const params = new URLSearchParams(searchParams);
        if (contentType === 'movie') {
          params.set('movie', contentId);
          params.delete('series');
        } else {
          params.set('series', contentId);
          params.delete('movie');
        }
        setSearchParams(params, { replace: true });
      }
    } catch (error) {
      console.error('Error fetching content details:', error);
    } finally {
      isOpeningRef.current = false;
    }
  };

  const handleMovieClick = async (movie) => {
    // We'll open via the URL effect, but we can also call directly
    const params = new URLSearchParams(searchParams);
    params.set('movie', movie.id);
    params.delete('series');
    setSearchParams(params, { replace: true });
  };

  // ---- Handle media change inside modal (update URL) ----
  const handleMediaChange = (media, mediaType) => {
    setSelectedMedia(media);
    setSelectedMediaType(mediaType);
    const params = new URLSearchParams(searchParams);
    if (mediaType === 'movie') {
      params.set('movie', media.id);
      params.delete('series');
    } else {
      params.set('series', media.id);
      params.delete('movie');
    }
    setSearchParams(params, { replace: true });
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedMedia(null);
    setSelectedMediaType(null);
    const params = new URLSearchParams(searchParams);
    params.delete('movie');
    params.delete('series');
    setSearchParams(params, { replace: true });
  };

  const minimizeFilters = () => setFiltersMinimized(true);
  const expandFilters = () => setFiltersMinimized(false);

  const handleSearch = () => {
    setCurrentPage(1);
    minimizeFilters();
  };

  return (
    <div className="page-content active" id="moviesPage">
      {loading && (
        <div className="loading-overlay">
          <img src="/loading2.png" alt="Loading..." className="blinking-logo" />
        </div>
      )}

      {!loading && (
        <div className="movies-content">
          <div
            id="collapsedFiltersBtn"
            className="collapsed-filters-btn"
            style={{ display: filtersMinimized ? 'flex' : 'none' }}
            ref={collapsedFiltersBtnRef}
            onClick={expandFilters}
          >
            <div className="filters-text">{t('filterLabel')}</div>
            <div className="active-filters-text">{getActiveFiltersText()}</div>
          </div>

          <div style={{ display: filtersMinimized ? 'none' : 'flex' }} ref={filtersRef}>
            <Filters
              type="movies"
              onGenreChange={setGenre}
              onLanguageChange={setLanguage}
              onYearChange={setYear}
              onSortChange={setSort}
              onProviderChange={setProvider}
              onSearch={handleSearch}
              genreValue={genre}
              languageValue={language}
              yearValue={year}
              sortValue={sort}
              providerValue={provider}
              lang={lang}
              translations={translations}
            />
          </div>

          <div id="moviesGrid" className="movies-grid">
            {movies.length === 0 ? (
              <div className="empty-page" style={{ gridColumn: '1 / -1', padding: '50px' }}>
                <i className="fas fa-film"></i>
                <p>{t('noMoviesFound')}</p>
              </div>
            ) : (
              movies.map(movie =>
                BLOCKED_MOVIE_IDS.includes(movie.id) ? (
                  <div key={movie.id} className="blocked-movie-card">
                    <div className="blocked-poster">
                      <i className="fas fa-clapperboard"></i>
                      <p className="release-text">To Be Released</p>
                    </div>
                  </div>
                ) : (
                  <MovieCard
                    key={movie.id}
                    movie={movie}
                    onClick={handleMovieClick}
                    releaseYear={movie.release_year}
                    runtime={movie.runtime}
                    ratingFormat={ratingFormat}
                  />
                )
              )
            )}
          </div>

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPrev={() => handlePaginationAction(() => {
              if (currentPage > 1) {
                setCurrentPage(currentPage - 1);
                window.scrollTo({ top: 0, behavior: 'smooth' });
                if (!filtersMinimized) minimizeFilters();
              }
            })}
            onNext={() => handlePaginationAction(() => {
              if (currentPage < totalPages) {
                setCurrentPage(currentPage + 1);
                window.scrollTo({ top: 0, behavior: 'smooth' });
                if (!filtersMinimized) minimizeFilters();
              }
            })}
            onPageChange={(page) => handlePaginationAction(() => {
              if (page >= 1 && page <= totalPages && page !== currentPage) {
                setCurrentPage(page);
                window.scrollTo({ top: 0, behavior: 'smooth' });
                if (!filtersMinimized) minimizeFilters();
              }
            })}
            cooldown={paginationCooldown}
            type="movies"
          />

          {/* Unified Media Modal */}
          {isModalOpen && selectedMedia && (
            <MediaModal
              media={selectedMedia}
              mediaType={selectedMediaType}
              onClose={closeModal}
              onMediaChange={handleMediaChange}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default MoviesPage;